import * as XLSX from "xlsx"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import dayjs from "dayjs"
import moment from "moment"

// --- Removed Font Imports ---

const sanitizeString = (value) => {
  if (typeof value !== "string") {
    return value
  }
  // Basic sanitization: remove control chars, trim, normalize spaces
  const cleaned = value
    .replace(/\p{Cc}/gu, "")
    .trim()
    .replace(/\s+/g, " ")
  return cleaned
}

// Helper function to replace Latvian diacritics with ASCII equivalents
const transliterateLatvian = (str) => {
  if (typeof str !== "string") {
    return str
  }
  // Normalize to NFD (Canonical Decomposition) to separate base chars and diacritics
  // Then remove diacritic marks (Unicode range U+0300 to U+036F)
  // Also replace specific characters like Ł/ł if needed (though not standard Latvian)
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/Ł/g, "L") // Example for Polish L if it appears
    .replace(/ł/g, "l")
}

export const parseNumericValue = (value) => {
  if (value === null || value === undefined) return null
  if (typeof value === "number") return value
  if (typeof value !== "string") return null

  // 1. Remove control characters and trim
  let cleaned = value.replace(/\p{Cc}/gu, "").trim()
  // 2. Explicitly remove the narrow non-breaking space (U+202F) and regular spaces
  cleaned = cleaned.replace(/[\s\u202f]/g, "")
  // 3. Remove any remaining characters that are NOT digits, comma, period, or hyphen
  cleaned = cleaned.replace(/[^\d.,\-+]/g, "")
  // 4. Standardize decimal separator (comma to period)
  cleaned = cleaned.replace(/,/g, ".")
  // 5. Ensure only one decimal point remains (last one)
  const parts = cleaned.split(".")
  if (parts.length > 1) {
    cleaned = parts.slice(0, -1).join("") + "." + parts.slice(-1)
  }
  // 6. Attempt to parse
  const num = parseFloat(cleaned)
  // Return number or null if parsing failed
  return isNaN(num) ? null : num
}

const getColumnValue = (record, column) => {
  let rawValue
  if (column.exportValue) {
    rawValue = column.exportValue(
      column.dataIndex
        ? Array.isArray(column.dataIndex)
          ? record[column.dataIndex[0]]?.[column.dataIndex[1]]
          : record[column.dataIndex]
        : record,
      record
    )
  } else if (column.dataIndex) {
    rawValue = Array.isArray(column.dataIndex)
      ? record[column.dataIndex[0]]?.[column.dataIndex[1]]
      : record[column.dataIndex]
  } else {
    rawValue = ""
  }

  // Ensure rawValue is a string for processing
  const stringValue = String(rawValue === null || rawValue === undefined ? "" : rawValue)

  if (column.dataType === "numeric") {
    // Pass the original string directly to parseNumericValue
    return parseNumericValue(stringValue)
  } else {
    // Sanitize only non-numeric strings here
    return sanitizeString(stringValue)
  }
}

// Removed moneyFormatter parameter
const getColumnValueFormatted = (record, column, forPdf = false) => {
  let value
  if (column.exportValueFormatted) {
    let originalValueSource = column.dataIndex
      ? Array.isArray(column.dataIndex)
        ? record[column.dataIndex[0]]?.[column.dataIndex[1]]
        : record[column.dataIndex]
      : record
    if (column.exportValue) {
      originalValueSource = column.exportValue(originalValueSource, record)
    }
    // Get the potentially formatted value
    value = column.exportValueFormatted(originalValueSource, record)

    // If the formatter returned a string for a numeric column,
    // parse it back to ensure consistency.
    if (typeof value === "string" && column.dataType === "numeric") {
      const parsedNum = parseNumericValue(value) // Use our robust parser
      // Use the parsed number if successful, otherwise keep the original string
      value = parsedNum !== null ? parsedNum : String(value)
    } else {
      // Ensure it's a string or number otherwise
      value = value === null || value === undefined ? "—" : value
    }
  } else {
    // Get the potentially parsed or sanitized value from getColumnValue
    value = getColumnValue(record, column)
  }

  // Apply standard date formatting if applicable
  if (value instanceof Date) {
    value = moment(value).format("DD.MM.YYYY")
  } else if (value === null || value === undefined || value === "null") {
    value = "—"
  }

  // --- Apply transliteration specifically for PDF output ---
  // Ensure value is treated as string before transliteration
  // Skip transliteration for numeric columns
  if (forPdf && column.dataType !== "numeric") {
    value = transliterateLatvian(String(value))
  } else {
    // Ensure value is still a string if not transliterated
    // Use toFixed(2) for numbers to control decimals, otherwise convert to string
    if (typeof value === "number") {
      value = value.toFixed(2) // Control decimals for numbers
    } else {
      value = String(value)
    }
  }

  return value
}

export const exportToExcel = (data, columns, fileNameBase) => {
  const fileName = `${fileNameBase}_${dayjs().format("YYYYMMDD")}.xlsx`
  const exportColumns = columns.filter((col) => !col.noExport)
  const excelData = data.map((record) => {
    const row = {}
    exportColumns.forEach((col) => {
      let value
      // Get the raw parsed/sanitized value first
      const rawValue = getColumnValue(record, col)

      if (col.dataType === "numeric") {
        // Use the number directly if available
        value = typeof rawValue === "number" ? rawValue : null // Default to null if not a number
      } else {
        // Get the formatted string value (no PDF transliteration, no money formatter)
        value = getColumnValueFormatted(record, col, false) // Pass false for forPdf
      }

      // Handle potential nulls or specific Excel formatting
      if (value === null || value === undefined) {
        value = "" // Use empty string for null/undefined in Excel
      } else if (value instanceof Date) {
        // Should be formatted string already
        // value = moment(value).format("DD.MM.YYYY");
      } else if (typeof value === "object") {
        // Catch any remaining objects
        value = JSON.stringify(value)
      }
      row[col.title || col.key] = value
    })
    return row
  })
  const ws = XLSX.utils.json_to_sheet(excelData)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1")
  XLSX.writeFile(wb, fileName)
}

export const exportToPdf = (data, columns, fileNameBase, options = {}) => {
  const {
    title = "Report",
    totalLabel = "",
    totalValue = "", // Keep receiving totalValue, format below
    orientation = "portrait",
    theme = "grid",
    headStyles = { fillColor: [22, 160, 133] },
    styles = { fontSize: 8, cellPadding: 2 },
    columnStyles = {},
    // Removed moneyFormatter from options
  } = options

  const fileName = `${fileNameBase}_${dayjs().format("YYYYMMDD")}.pdf`
  const doc = new jsPDF({ orientation })

  const exportColumns = columns.filter((col) => !col.noExport)
  const head = [exportColumns.map((col) => transliterateLatvian(col.title || col.key))]
  const body = data.map((record) => {
    // Pass true for the 'forPdf' flag to enable transliteration (where applicable)
    // No moneyFormatter passed here
    return exportColumns.map((col) => getColumnValueFormatted(record, col, true))
  })

  doc.setFontSize(16)
  doc.text(transliterateLatvian(title), 14, 15)
  doc.setFontSize(10)
  doc.text(`Generated on: ${dayjs().format("DD.MM.YYYY HH:mm")}`, 14, 20)

  const tableStyles = { ...styles }
  const tableHeadStyles = { ...headStyles }

  autoTable(doc, {
    startY: 25,
    head: head,
    body: body,
    theme: theme,
    styles: tableStyles,
    headStyles: tableHeadStyles,
    columnStyles: columnStyles,
  })

  if (totalLabel && totalValue !== null && totalValue !== undefined) {
    const finalY = doc.lastAutoTable.finalY
    doc.setFontSize(12)
    // Format totalValue as string with fixed decimals before potential transliteration
    const formattedTotalValue =
      typeof totalValue === "number" ? totalValue.toFixed(2) : String(totalValue)
    doc.text(
      `${transliterateLatvian(totalLabel)} ${transliterateLatvian(formattedTotalValue)}`,
      14,
      finalY + 10
    )
  }

  doc.save(fileName)
}
